<!-- category 설정 및 필터 기능 + 검색 및 상세내용 입력 + 소 중 대 목표 설정 및 제촉 시스템 등등... -->

<template>
  <div class="dialog main-calendar">
    <span class="cls" @click="oth_showMainCalendar(false)"></span>
    <div class="paper">

      <!-- Calendar Header -->
      <div class="calendar-header">
        <div class="header-left">
          <button class="nav-btn" @click="prevMonth"><i class="ph ph-caret-left"></i></button>
          <span class="month-title">{{ month + 1 }}월 <small>{{ year }}</small></span>
          <button class="nav-btn" @click="nextMonth"><i class="ph ph-caret-right"></i></button>
          <span class="header-date">{{ year }}.{{ String(month + 1).padStart(2, '0') }}.{{ String(today.getDate()).padStart(2, '0') }}.{{ ['SUN','MON','TUE','WED','THU','FRI','SAT'][today.getDay()] }}</span>
        </div>
        <div class="header-right">
          <button class="filter-btn" :class="{ active: calendarMode === 'schedule' }" @click="calendarMode = 'schedule'"><i class="ph-fill ph-calendar-dots"></i> 일정</button>
          <button class="filter-btn" :class="{ active: calendarMode === 'pounch' }" @click="calendarMode = 'pounch'"><i class="ph-fill ph-check-square"></i> 출석</button>
          <button class="filter-btn" @click="goToday">오늘</button>
        </div>
      </div>

      <!-- Filter Bar (schedule mode) -->
      <div class="filter-bar" v-if="calendarMode === 'schedule'">
        <div class="filter-group" v-for="fd in filterDefs" :key="fd.key">
          <span class="filter-label">{{ fd.label }}</span>
          <button 
            v-for="opt in fd.options" 
            :key="opt.value"
            class="filter-chip"
            :class="{ active: todoFilters[fd.key] === opt.value }"
            @click="setFilter(fd.key, opt.value)"
          >{{ opt.label }}</button>
        </div>
        <button class="filter-reset" v-if="activeFilterCount > 0" @click="resetFilters">
          <i class="ph ph-x"></i> 초기화
        </button>
      </div>

      <!-- Calendar + Todo Layout -->
      <div class="calendar-layout">

        <!-- Calendar Grid -->
        <div class="calendar-area">
          <div class="calendar-grid">
            <div 
              class="day-name" 
              v-for="(day, i) in dayNames" 
              :key="day"
              :class="{ 'sunday': i === 0, 'saturday': i === 6 }"
            >
              {{ day }}
            </div>

            <div 
              class="day" 
              v-for="(dayObj, index) in daysInMonth" 
              :key="index"
              :class="{ 
                today: isToday(dayObj.date),
                'other-month': !dayObj.isCurrentMonth,
                'selected': selectedDate && getDateKey(selectedDate) === getDateKey(dayObj.date),
                'saturday': dayObj.date.getDay() === 6,
                'sunday': dayObj.date.getDay() === 0,
                'holiday': isHoliday(dayObj.date)
              }"
              @click="dayObj.isCurrentMonth && selectDate(dayObj.date)"
            >
              <div class="day-number">
                {{ dayObj.date.getDate() }}
                <span class="holiday-name" v-if="getHolidayName(dayObj.date)">{{ getHolidayName(dayObj.date) }}</span>
              </div>

              <!-- 출석 모드 -->
              <template v-if="calendarMode === 'pounch'">
                <div class="pounch-info" v-if="getPounchForDate(dayObj.date)">
                  <span class="badge check-in" v-if="getPounchForDate(dayObj.date).check_in">
                    IN {{ formatTime(getPounchForDate(dayObj.date).check_in) }}
                  </span>
                  <span class="badge check-out" v-if="getPounchForDate(dayObj.date).check_out">
                    OUT {{ formatTime(getPounchForDate(dayObj.date).check_out) }}
                  </span>
                </div>
                <div class="pounch-info" v-else-if="isPastWorkday(dayObj.date) && dayObj.isCurrentMonth">
                  <span class="badge absent">미기록</span>
                </div>
              </template>

              <!-- 일정 모드 -->
              <template v-else-if="calendarMode === 'schedule'">
                <div class="todo-tags" v-if="getTodosForDate(dayObj.date).length > 0">
                  <template v-for="(todo, n) in getTodosForDate(dayObj.date)" :key="todo.idx">
                    <div 
                      v-if="n < todoMaxCnt"
                      class="todo-tag"
                      :class="{ completed: todo.is_completed }"
                    >
                      {{ todo.title }}
                    </div>
                  </template>
                  <div v-if="getTodosForDate(dayObj.date).length > todoMaxCnt" class="more-todos">
                    +{{ getTodosForDate(dayObj.date).length - todoMaxCnt }}
                  </div>
                </div>
              </template>
            </div>
          </div>
        </div>

        <!-- Todo Section (Side Panel) -->
        <div class="todo-section" :class="{ open: selectedDate }">
          <div v-if="selectedDate" class="todo-inner">
            <div class="todo-header">
              <h3 class="todo-title">
                <i class="ph-fill ph-calendar"></i>
                {{ selectedDate.getMonth() + 1 }}월 {{ selectedDate.getDate() }}일
              </h3>
              <button class="todo-close" @click="selectedDate = null"><i class="ph ph-x"></i></button>
            </div>
            
            <div class="todo-input-group">
              <input
                v-model="newTodoText"
                @keypress.enter="addTodo"
                type="text"
                placeholder="새로운 할 일..."
                class="todo-input"
                maxlength="50"
              />
              <button @click="addTodo" class="add-btn"><i class="ph-bold ph-plus"></i></button>
            </div>

            <div class="todo-list">
              <div 
                v-for="todo in getTodosForDate(selectedDate)" 
                :key="todo.idx"
                class="todo-item"
                :class="{ completed: todo.is_completed }"
              >
                <button 
                  @click="toggleTodo(todo)"
                  class="check-btn"
                  :class="{ checked: todo.is_completed }"
                >
                  <span v-if="todo.is_completed">✓</span>
                </button>

                <!-- 수정 모드 -->
                <template v-if="editingTodo && editingTodo.idx === todo.idx">
                  <input 
                    v-model="editingTodo.title"
                    @keyup.enter="saveEdit(todo)"
                    @blur="saveEdit(todo)"
                    class="edit-input"
                    maxlength="50"
                    autofocus
                  />
                </template>

                <!-- 일반 모드 -->
                <template v-else>
                  <div class="todo-content">
                    <span class="todo-text" @dblclick="startEdit(todo)">{{ todo.title }}</span>
                    <span class="todo-due">{{ formatDueDate(todo.due_date) }}</span>
                  </div>
                </template>

                <button 
                  @click="openShareDialog(todo)"
                  class="share-btn"
                  title="팀원에게 공유"
                >
                  <i class="ph ph-share-network"></i>
                </button>
                <button 
                  @click="deleteTodo(todo.idx)"
                  class="delete-btn"
                >
                  <i class="ph ph-trash"></i>
                </button>
              </div>

              <div v-if="getTodosForDate(selectedDate).length === 0" class="empty-message">
                <i class="ph ph-calendar-blank" style="font-size:32px;opacity:0.3"></i>
                <p>등록된 일정이 없습니다</p>
              </div>
            </div>

            <!-- 추천 일정 -->
            <div class="suggest-section">
              <p class="suggest-title">💡 추천 일정</p>
              <div class="suggest-list">
                <div 
                  v-for="(suggest, idx) in suggestedTodos" 
                  :key="idx"
                  class="suggest-item"
                  @click="addSuggestedTodo(suggest)"
                >
                  <span class="suggest-icon">{{ suggest.icon }}</span>
                  <span class="suggest-text">{{ suggest.title }}</span>
                  <i class="ph ph-plus-circle"></i>
                </div>
              </div>
            </div>
          </div>

          <!-- 공유 다이얼로그 -->
          <div class="share-overlay" v-if="shareDialog.visible" @click.self="shareDialog.visible = false">
            <div class="share-panel">
              <div class="share-panel-header">
                <h4>할일 공유</h4>
                <button class="todo-close" @click="shareDialog.visible = false"><i class="ph ph-x"></i></button>
              </div>
              <p class="share-todo-title">{{ shareDialog.todo?.title }}</p>
              <div v-if="shareMembers.length === 0" class="share-empty">
                <p>소속된 조직이 없거나 공유 가능한 팀원이 없습니다.</p>
              </div>
              <div v-else class="share-member-list">
                <label 
                  v-for="m in shareMembers" 
                  :key="m.mb_id" 
                  class="share-member-item"
                  :class="{ selected: shareSelected.includes(m.mb_id) }"
                >
                  <input type="checkbox" :value="m.mb_id" v-model="shareSelected" />
                  <img :src="m.image || '/imgs/unknow_user.png'" class="share-avatar" />
                  <div class="share-member-info">
                    <span class="share-member-name">{{ m.name || m.nickname || m.mb_id }}</span>
                    <span class="share-member-role">{{ m.role_name || '' }}</span>
                  </div>
                </label>
              </div>
              <button 
                class="share-send-btn" 
                :disabled="shareSelected.length === 0" 
                @click="sendShare"
              >
                <i class="ph ph-paper-plane-tilt"></i>
                {{ shareSelected.length }}명에게 공유
              </button>
            </div>
          </div>

          <!-- 날짜 미선택 시 -->
          <div v-else class="todo-empty-state">
            <i class="ph ph-hand-tap" style="font-size:48px;opacity:0.2"></i>
            <p>날짜를 선택하세요</p>
          </div>
        </div>

      </div>

    </div>
  </div>
</template>

<script>
import { ref, computed, inject, onMounted, onBeforeUnmount } from 'vue'
import axios from 'axios';

import { useBackButton } from '@/utils/useBackBtn';

export default {
  name: 'MainCalendar',
  props: {
    oth_showMainCalendar: {
      type: Boolean,
      required: true,
    },
  },
  setup(props) {
    const getMbid = inject('getMbid')
    const sql_query = inject('sql_query')
    const today = new Date()
    const year = ref(today.getFullYear())
    const month = ref(today.getMonth())
    const todos = ref([])
    const selectedDate = ref(null)
    const newTodoText = ref('')
    const editingTodo = ref(null)
    const holidays = ref([])
    const calendarMode = ref('schedule')
    const pounchs = ref(null)

    // 공유 관련
    const shareDialog = ref({ visible: false, todo: null })
    const shareMembers = ref([])
    const shareSelected = ref([])

    useBackButton(() => {
      props.oth_showMainCalendar(false);
    });

    const todoMaxCnt = 2;

    // ─── Extensible Filter System ───
    const todoFilters = ref({
      priority: 'all',
      status: 'all',
    });

    const filterDefs = [
      {
        key: 'priority',
        label: '우선순위',
        options: [
          { value: 'all', label: '전체' },
          { value: 0, label: '🔴 높음' },
          { value: 1, label: '🟡 보통' },
          { value: 2, label: '🔵 낮음' },
        ]
      },
      {
        key: 'status',
        label: '상태',
        options: [
          { value: 'all', label: '전체' },
          { value: 'undone', label: '미완료' },
          { value: 'done', label: '완료' },
        ]
      },
    ];

    function setFilter(key, value) {
      todoFilters.value[key] = value;
    }

    function resetFilters() {
      Object.keys(todoFilters.value).forEach(k => { todoFilters.value[k] = 'all'; });
    }

    const activeFilterCount = computed(() => {
      return Object.values(todoFilters.value).filter(v => v !== 'all').length;
    });

    const filteredTodos = computed(() => {
      return todos.value.filter(t => {
        const f = todoFilters.value;
        if (f.priority !== 'all' && Number(t.priority) !== f.priority) return false;
        if (f.status === 'done' && !t.is_completed) return false;
        if (f.status === 'undone' && t.is_completed) return false;
        return true;
      });
    });

    // 공휴일
    async function fetchHolidays(yr) {
      const cacheKey = `holidays_${yr}`;
      const cached = localStorage.getItem(cacheKey);
      if (cached) return JSON.parse(cached);

      const url = 'https://www.googleapis.com/calendar/v3/calendars/ko.south_korea.official%23holiday%40group.v.calendar.google.com/events';
      const params = {
        key: 'AIzaSyCzyeRoKk3Esst540q17k_7rbeD5Ct0cr0',
        orderBy: 'startTime',
        singleEvents: true,
        timeMin: `${yr}-01-01T00:00:00Z`,
        timeMax: `${yr + 1}-01-01T00:00:00Z`,
      };
      const res = await axios.get(url, { params });
      const items = res.data.items || [];
      const holidaysData = items.map(h => ({
        title: h.summary,
        date: h.start.date,
      }));
      localStorage.setItem(cacheKey, JSON.stringify(holidaysData));
      return holidaysData;
    }

    function isHoliday(date) {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
      return holidays.value.some(h => h.date === dateStr);
    }

    function getHolidayName(date) {
      const dateStr = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
      return holidays.value.find(h => h.date === dateStr)?.title || '';
    }

    // 출석 데이터
    async function fetchPounchs() {
      let sql = `select * from attendance where mb_id = '${getMbid()}'`;
      let res = await sql_query(sql);
      pounchs.value = res;
    }

    function getPounchForDate(date) {
      if (!pounchs.value) return null;
      const dateKey = getDateKey(date);
      return pounchs.value.find(p => p.work_date && p.work_date.slice(0, 10) === dateKey);
    }

    function formatTime(datetime) {
      if (!datetime) return '';
      const timeStr = typeof datetime === 'string' 
        ? datetime.split('T')[1] || datetime.split(' ')[1] 
        : datetime.toISOString().split('T')[1];
      return timeStr ? timeStr.slice(0, 5) : '';
    }

    function isPastWorkday(date) {
      const day = date.getDay();
      const isWeekend = day === 0 || day === 6;
      const isPast = date < new Date(today.getFullYear(), today.getMonth(), today.getDate());
      const dateStr = getDateKey(date);
      const isHol = holidays.value.some(h => h.date === dateStr);
      return !isWeekend && !isHol && isPast;
    }

    onMounted(async () => {
      holidays.value = await fetchHolidays(new Date().getFullYear());
      await fetchTodos();
      await fetchPounchs();
      window.addEventListener('attendance-updated', fetchPounchs);
      window.addEventListener('todos-updated', fetchTodos);
    });

    onBeforeUnmount(() => {
      window.removeEventListener('attendance-updated', fetchPounchs);
      window.removeEventListener('todos-updated', fetchTodos);
    });

    function startEdit(todo) {
      editingTodo.value = { ...todo }
    }

    async function saveEdit(todo) {
      if (!editingTodo.value) return
      const newTitle = editingTodo.value.title.trim()
      if (newTitle === '') {
        editingTodo.value = null
        return
      }
      await sql_query(`
        UPDATE todos 
        SET title = '${newTitle}', updated_at = NOW() 
        WHERE idx = '${todo.idx}'
      `)
      editingTodo.value = null
      await fetchTodos()
      window.dispatchEvent(new Event('todos-updated'))
    }

    async function fetchTodos() {
      const res = await sql_query(`SELECT * FROM todos WHERE mb_id = '${getMbid()}' ORDER BY priority, created_at`);
      todos.value = res || [];
    }

    // 요일명
    const dayNames = ['일', '월', '화', '수', '목', '금', '토']

    // 월별 날짜 계산
    const daysInMonth = computed(() => {
      const firstDay = new Date(year.value, month.value, 1)
      const lastDay = new Date(year.value, month.value + 1, 0)
      const days = []

      for (let i = 0; i < firstDay.getDay(); i++) {
        const prevDate = new Date(year.value, month.value, i - firstDay.getDay() + 1)
        days.push({ date: prevDate, isCurrentMonth: false })
      }

      for (let i = 1; i <= lastDay.getDate(); i++) {
        days.push({ date: new Date(year.value, month.value, i), isCurrentMonth: true })
      }

      return days
    })

    // 월 이동
    function prevMonth() {
      if (month.value === 0) {
        month.value = 11
        year.value--
      } else month.value--
    }
    function nextMonth() {
      if (month.value === 11) {
        month.value = 0
        year.value++
      } else month.value++
    }

    function goToday() {
      const now = new Date()
      year.value = now.getFullYear()
      month.value = now.getMonth()
      selectDate(now)
    }

    // 오늘 날짜 체크
    function isToday(date) {
      return (
        date.getDate() === today.getDate() &&
        date.getMonth() === today.getMonth() &&
        date.getFullYear() === today.getFullYear()
      )
    }

    // 날짜 key 만들기
    function getDateKey(date) {
      if (typeof date === 'string') {
        return date.slice(0, 10);
      }
      const d = new Date(date);
      const y = d.getFullYear();
      const m = String(d.getMonth() + 1).padStart(2, '0');
      const dy = String(d.getDate()).padStart(2, '0');
      return `${y}-${m}-${dy}`;
    }

    // 날짜 선택
    function selectDate(date) {
      selectedDate.value = date
    }

    function getTodosForDate(date) {
      const key = getDateKey(date)
      return filteredTodos.value.filter(t => getDateKey(t.due_date) === key)
    }

    async function addTodo() {
      if (!newTodoText.value.trim()) return
      const dueDate = selectedDate.value || today
      const title = newTodoText.value.trim()
      
      const y = dueDate.getFullYear()
      const m = String(dueDate.getMonth() + 1).padStart(2, '0')
      const d = String(dueDate.getDate()).padStart(2, '0')
      const dueDateStr = `${y}-${m}-${d} 00:00:00`

      await sql_query(`
        INSERT INTO todos (mb_id, title, is_completed, due_date, created_at, updated_at)
        VALUES ('${getMbid()}', '${title}', 0, '${dueDateStr}', NOW(), NOW())
      `)

      newTodoText.value = ''
      await fetchTodos()
      window.dispatchEvent(new Event('todos-updated'))
    }

    async function toggleTodo(todo) {
      const newStatus = todo.is_completed ? 0 : 1
      todo.is_completed = newStatus
      await sql_query(`UPDATE todos SET is_completed = '${newStatus}' WHERE idx = '${todo.idx}'`)

      const aScore = Number(todo.activity_score) || 5
      if (aScore > 0) {
        if (newStatus === 1) {
          await sql_query(`INSERT INTO activity_score_log (mb_id, score, source_type, source_idx, reason, created_at) VALUES ('${getMbid()}', ${aScore}, 'todo', ${todo.idx}, '${(todo.title||"").replace(/'/g,"''")} 완료', NOW())`)
        } else {
          await sql_query(`DELETE FROM activity_score_log WHERE mb_id='${getMbid()}' AND source_type='todo' AND source_idx=${todo.idx} ORDER BY idx DESC LIMIT 1`)
        }
      }

      await fetchTodos()
      window.dispatchEvent(new Event('todos-updated'))
    }

    async function deleteTodo(todoId) {
      if (!confirm('정말 삭제하시겠습니까?')) return
      todos.value = todos.value.filter(t => t.idx !== todoId)
      await sql_query(`DELETE FROM todos WHERE idx = ${todoId}`)
      await fetchTodos()
      window.dispatchEvent(new Event('todos-updated'))
    }

    // 추천 할일
    const suggestPool = [
      { icon: '📞', title: '고객 3명 안부 전화' },
      { icon: '📝', title: '신규 고객 상담 준비' },
      { icon: '📊', title: '이번 주 실적 정리' },
      { icon: '🤝', title: '소개 요청 연락하기' },
      { icon: '📚', title: '신상품 교육자료 읽기' },
      { icon: '💌', title: '감사 메시지 보내기' },
      { icon: '🗓️', title: '다음 주 일정 계획' },
      { icon: '🏥', title: '보장분석 리포트 작성' },
      { icon: '🎂', title: '이번 달 생일 고객 확인' },
      { icon: '🔍', title: '기존 고객 보장 점검' },
    ];

    const suggestedTodos = computed(() => {
      const targetDate = selectedDate.value || today;
      const dateKey = getDateKey(targetDate);
      const existingTitles = getTodosForDate(targetDate).map(t => t.title);
      const available = suggestPool.filter(s => !existingTitles.includes(s.title));
      const seed = dateKey.split('-').reduce((a, b) => a + Number(b), 0);
      const shuffled = [...available].sort((a, b) => {
        return ((a.title.charCodeAt(0) * seed) % 100) - ((b.title.charCodeAt(0) * seed) % 100);
      });
      return shuffled.slice(0, 3);
    });

    async function addSuggestedTodo(suggest) {
      const dueDate = selectedDate.value || today;
      const y = dueDate.getFullYear();
      const m = String(dueDate.getMonth() + 1).padStart(2, '0');
      const d = String(dueDate.getDate()).padStart(2, '0');
      const dueDateStr = `${y}-${m}-${d} 00:00:00`;
      await sql_query(`
        INSERT INTO todos (mb_id, title, is_completed, due_date, created_at, updated_at)
        VALUES ('${getMbid()}', '${suggest.title}', 0, '${dueDateStr}', NOW(), NOW())
      `);
      await fetchTodos();
      window.dispatchEvent(new Event('todos-updated'));
    }

    function formatDueDate(dateStr) {
      if (!dateStr) return '';
      const datePart = String(dateStr).slice(0, 10);
      const [y, m, d] = datePart.split('-').map(Number);
      const localDate = new Date(y, m - 1, d);
      const dayLabels = ['일', '월', '화', '수', '목', '금', '토'];
      return `${m}/${d} (${dayLabels[localDate.getDay()]})`;
    }

    // 공유 관련 함수
    function openShareDialog(todo) {
      shareDialog.value = { visible: true, todo }
      shareSelected.value = []
      fetchShareMembers()
    }

    async function fetchShareMembers() {
      try {
        const grp = await sql_query(`SELECT main_group_idx, group_node_idx FROM group_member WHERE mb_id='${getMbid()}' AND status='join' LIMIT 1`)
        if (!grp?.[0]) { shareMembers.value = []; return }
        const { main_group_idx } = grp[0]
        const rows = await sql_query(`
          SELECT gm.mb_id, m.name, m.nickname, m.image, r.name as role_name
          FROM group_member gm
          LEFT JOIN member m ON gm.mb_id = m.mb_id
          LEFT JOIN group_role r ON gm.role_idx = r.idx
          WHERE gm.main_group_idx = '${main_group_idx}'
            AND gm.status = 'join'
            AND gm.mb_id != '${getMbid()}'
          ORDER BY m.name
        `)
        shareMembers.value = Array.isArray(rows) ? rows : []
      } catch(e) {
        console.warn('팀원 로드 실패:', e)
        shareMembers.value = []
      }
    }

    async function sendShare() {
      const todo = shareDialog.value.todo
      if (!todo || shareSelected.value.length === 0) return
      const dueStr = todo.due_date ? String(todo.due_date).slice(0, 19) : null
      for (const targetId of shareSelected.value) {
        await sql_query(`
          INSERT INTO todos (mb_id, title, is_completed, due_date, priority, shared_by, source_type, created_at, updated_at)
          VALUES ('${targetId}', '${(todo.title || '').replace(/'/g, "''")}', 0, ${dueStr ? "'" + dueStr + "'" : 'NULL'}, '${todo.priority || 1}', '${getMbid()}', 'shared', NOW(), NOW())
        `)
      }
      shareDialog.value.visible = false
      alert(`${shareSelected.value.length}명에게 공유했습니다.`)
    }

    return {
      today,
      year,
      month,
      dayNames,
      daysInMonth,
      prevMonth,
      nextMonth,
      goToday,
      isToday,
      selectedDate,
      selectDate,
      getTodosForDate,
      newTodoText,
      addTodo,
      toggleTodo,
      deleteTodo,
      getDateKey,
      editingTodo,
      startEdit,
      saveEdit,
      shareDialog,
      shareMembers,
      shareSelected,
      openShareDialog,
      sendShare,
      holidays,
      todoMaxCnt,
      calendarMode,
      isHoliday,
      getHolidayName,
      getPounchForDate,
      formatTime,
      isPastWorkday,
      suggestedTodos,
      addSuggestedTodo,
      formatDueDate,
      todoFilters,
      filterDefs,
      setFilter,
      resetFilters,
      activeFilterCount,
    }
  },
}
</script>


<style scoped>
.dialog {
  position: fixed;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(8px);
}

.dialog > .cls {
  width: 100%;
  height: 100%;
  position: absolute;
  left: 0;
  top: 0;
  z-index: -1;
}

.dialog > .paper {
  width: 94%;
  max-width: 1200px;
  max-height: 92vh;
  height: 100%;
  background: var(--color-background);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 20px;
  padding: 24px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow: hidden;
  box-shadow: 0 24px 80px rgba(0,0,0,0.4);
}

.dialog > .paper::-webkit-scrollbar {
  width: 0px;
}

/* ===== Header ===== */
.calendar-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-btn {
  width: 36px;
  height: 36px;
  border: none;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
}

.nav-btn:hover {
  background: var(--color-surface-reverse);
  transform: scale(1.05);
}

.month-title {
  font-size: 24px;
  font-weight: 800;
  color: var(--color-text-primary);
  margin: 0 4px;
}

.month-title > small {
  font-size: 13px;
  font-weight: 400;
  opacity: 0.5;
}

.header-date {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-left: 4px;
}

.header-right {
  display: flex;
  gap: 6px;
}

.filter-btn {
  padding: 8px 14px;
  border: 1px solid rgba(255,255,255,0.08);
  background: var(--color-surface);
  color: var(--color-text-secondary);
  border-radius: 50px;
  cursor: pointer;
  font-size: 13px;
  font-weight: 500;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 5px;
}

.filter-btn:hover {
  border-color: var(--color-brand);
  color: var(--color-text-primary);
}

.filter-btn.active {
  background: var(--color-brand);
  color: var(--color-text-white, #fff);
  border-color: var(--color-brand);
  font-weight: 600;
}

/* ===== Filter Bar ===== */
.filter-bar {
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;
}

.filter-group {
  display: flex;
  align-items: center;
  gap: 4px;
}

.filter-label {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin-right: 2px;
  font-weight: 600;
  white-space: nowrap;
}

.filter-chip {
  padding: 4px 10px;
  border: 1px solid rgba(255,255,255,0.08);
  background: transparent;
  color: var(--color-text-secondary);
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.15s;
  white-space: nowrap;
}

.filter-chip:hover {
  border-color: var(--color-brand);
  color: var(--color-text-primary);
}

.filter-chip.active {
  background: var(--color-brand);
  color: #fff;
  border-color: var(--color-brand);
  font-weight: 600;
}

.filter-reset {
  padding: 4px 10px;
  border: none;
  background: rgba(239, 68, 68, 0.15);
  color: #ef4444;
  border-radius: 50px;
  cursor: pointer;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 3px;
  transition: all 0.15s;
  margin-left: auto;
}

.filter-reset:hover {
  background: rgba(239, 68, 68, 0.25);
}

/* ===== Calendar + Todo Layout ===== */
.calendar-layout {
  display: flex;
  gap: 16px;
  flex: 1;
  overflow: hidden;
  min-height: 0;
}

.calendar-area {
  flex: 1;
  overflow-y: auto;
  min-width: 0;
}

.calendar-area::-webkit-scrollbar {
  width: 0;
}

/* ===== Calendar Grid ===== */
.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, 1fr);
  gap: 2px;
}

.day-name {
  text-align: center;
  font-weight: 700;
  padding: 10px 4px;
  color: var(--color-text-secondary);
  font-size: 13px;
}

.day-name.sunday { color: #ef4444; }
.day-name.saturday { color: #3b82f6; }

.day {
  min-height: 80px;
  padding: 6px;
  cursor: pointer;
  transition: all 0.15s;
  background: var(--color-surface);
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  justify-content: start;
  overflow: hidden;
  border-radius: 8px;
  border: 2px solid transparent;
}

.day:hover {
  background: rgba(59, 130, 246, 0.06);
  border-color: rgba(59, 130, 246, 0.15);
}

.day.selected {
  border-color: #3b82f6;
  background: rgba(59, 130, 246, 0.08);
}

.day.today {
  border-color: #f59e0b;
  background: rgba(245, 158, 11, 0.06);
}

.day.today.selected {
  border-color: #3b82f6;
}

.day.other-month {
  opacity: 0.2;
  pointer-events: none;
}

.day-number {
  font-size: 13px;
  font-weight: 700;
  margin-bottom: 4px;
  color: var(--color-text-primary);
  display: flex;
  align-items: baseline;
  gap: 4px;
}

.day.today .day-number {
  color: #f59e0b;
}

.day.sunday .day-number { color: #ef4444; }
.day.saturday .day-number { color: #3b82f6; }
.day.holiday .day-number { color: #ef4444; }

.holiday-name {
  font-size: 9px;
  font-weight: 400;
  opacity: 0.7;
}

/* ===== 출석 정보 ===== */
.pounch-info {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 2px;
}

.pounch-info .badge {
  display: inline-block;
  padding: 2px 5px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: -0.3px;
  white-space: nowrap;
}

.badge.check-in {
  background: #dcfce7;
  color: #16a34a;
}

.badge.check-out {
  background: #dbeafe;
  color: #2563eb;
}

.badge.absent {
  background: #fee2e2;
  color: #dc2626;
}

/* ===== Todo Tags (Calendar Cell) ===== */
.todo-tags {
  display: flex;
  flex-direction: column;
  gap: 3px;
  margin-top: 2px;
}

.todo-tag {
  font-size: 10px;
  padding: 2px 5px;
  background: #dbeafe;
  color: #2563eb;
  border-radius: 4px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  border-left: 3px solid #3b82f6;
}

.todo-tag.completed {
  background: #dcfce7;
  color: #16a34a;
  border-left-color: #22c55e;
  text-decoration: line-through;
}

.more-todos {
  font-size: 9px;
  color: var(--color-text-secondary);
  font-weight: 600;
  padding: 1px 0;
}

/* ===== Todo Section (Side Panel) ===== */
.todo-section {
  width: 320px;
  min-width: 320px;
  background: var(--color-surface);
  border-radius: 16px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  transition: all 0.3s ease;
}

.todo-inner {
  display: flex;
  flex-direction: column;
  height: 100%;
  padding: 20px;
  gap: 14px;
  overflow: hidden;
}

.todo-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.todo-title {
  font-size: 17px;
  font-weight: 700;
  margin: 0;
  color: var(--color-text-primary);
  display: flex;
  align-items: center;
  gap: 6px;
}

.todo-close {
  width: 32px;
  height: 32px;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 18px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.15s;
}

.todo-close:hover {
  background: var(--color-surface-reverse);
}

.todo-input-group {
  display: flex;
  gap: 8px;
}

.todo-input {
  flex: 1;
  padding: 10px 14px;
  border: 1px solid rgba(255,255,255,0.08);
  background: var(--color-background);
  color: var(--color-text-primary);
  border-radius: 10px;
  font-size: 14px;
  transition: border-color 0.2s;
}

.todo-input:focus {
  outline: none;
  border-color: #3b82f6;
}

.add-btn {
  width: 40px;
  height: 40px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  cursor: pointer;
  font-size: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  flex-shrink: 0;
}

.add-btn:hover {
  background: #2563eb;
  transform: scale(1.05);
}

.todo-list {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  flex: 1;
  min-height: 0;
}

.todo-list::-webkit-scrollbar {
  width: 4px;
}

.todo-list::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.1);
  border-radius: 2px;
}

.todo-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 12px;
  background: var(--color-background);
  border-radius: 10px;
  transition: all 0.15s;
}

.todo-item:hover {
  background: rgba(59, 130, 246, 0.06);
}

.todo-item.completed {
  opacity: 0.55;
}

.check-btn {
  width: 22px;
  height: 22px;
  border: 2px solid rgba(255,255,255,0.15);
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s;
  color: white;
  flex-shrink: 0;
  font-size: 11px;
  padding: 0;
}

.check-btn:hover {
  border-color: #3b82f6;
}

.check-btn.checked {
  background: #22c55e;
  border-color: #22c55e;
}

.todo-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.todo-text {
  color: var(--color-text-primary);
  font-size: 14px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: text;
}

.todo-due {
  font-size: 11px;
  color: var(--color-text-secondary);
}

.todo-item.completed .todo-text {
  text-decoration: line-through;
  color: var(--color-text-secondary);
}

.delete-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
  opacity: 0;
  transition: all 0.15s;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.todo-item:hover .delete-btn {
  opacity: 1;
}

.delete-btn:hover {
  color: #ef4444;
  background: rgba(239, 68, 68, 0.1);
}

.empty-message {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  padding: 32px 0;
  color: var(--color-text-secondary);
  font-size: 13px;
}

.empty-message > p {
  margin: 0;
}

.todo-empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  gap: 12px;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.todo-empty-state > p {
  margin: 0;
  opacity: 0.5;
}

.edit-input {
  flex: 1;
  padding: 6px 10px;
  border: 1px solid #3b82f6;
  background: var(--color-surface);
  color: var(--color-text-primary);
  border-radius: 6px;
  font-size: 14px;
}

/* ===== 추천 일정 ===== */
.suggest-section {
  margin-top: auto;
  padding-top: 12px;
  border-top: 1px solid rgba(255,255,255,0.06);
}

.suggest-title {
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 0 0 8px 0;
}

.suggest-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.suggest-item {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  border-radius: 8px;
  background: transparent;
  border: 1px dashed rgba(255,255,255,0.08);
  cursor: pointer;
  transition: all 0.2s;
}

.suggest-item:hover {
  background: rgba(59, 130, 246, 0.06);
  border-color: var(--color-brand);
}

.suggest-item:active {
  transform: scale(0.97);
}

.suggest-icon {
  font-size: 14px;
}

.suggest-text {
  flex: 1;
  font-size: 12px;
  color: var(--color-text-secondary);
}

.suggest-item > i {
  font-size: 16px;
  color: var(--color-brand);
  opacity: 0;
  transition: opacity 0.2s;
}

.suggest-item:hover > i {
  opacity: 1;
}

/* ===== Share ===== */
.share-btn {
  width: 28px;
  height: 28px;
  border: none;
  background: none;
  color: var(--color-text-secondary);
  cursor: pointer;
  font-size: 14px;
  flex-shrink: 0;
  opacity: 0;
  transition: all 0.15s;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.todo-item:hover .share-btn {
  opacity: 1;
}

.share-btn:hover {
  color: #3b82f6;
  background: rgba(59, 130, 246, 0.1);
}

.share-overlay {
  position: fixed;
  left: 0; top: 0; right: 0; bottom: 0;
  background: rgba(0,0,0,0.5);
  z-index: 10000;
  display: flex;
  align-items: center;
  justify-content: center;
}

.share-panel {
  background: var(--color-background);
  border-radius: 16px;
  padding: 20px;
  width: 340px;
  max-height: 70vh;
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.3);
}

.share-panel-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.share-panel-header h4 {
  margin: 0;
  font-size: 16px;
  font-weight: 700;
  color: var(--color-text-primary);
}

.share-todo-title {
  margin: 0;
  font-size: 13px;
  color: var(--color-text-secondary);
  padding: 8px 10px;
  background: var(--color-surface);
  border-radius: 8px;
}

.share-empty {
  text-align: center;
  padding: 20px;
  color: var(--color-text-secondary);
  font-size: 13px;
}

.share-member-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 300px;
  overflow-y: auto;
}

.share-member-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 8px 10px;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s;
  border: 1px solid transparent;
}

.share-member-item:hover {
  background: rgba(59, 130, 246, 0.06);
}

.share-member-item.selected {
  background: rgba(59, 130, 246, 0.1);
  border-color: #3b82f6;
}

.share-member-item input[type="checkbox"] {
  display: none;
}

.share-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  object-fit: cover;
}

.share-member-info {
  display: flex;
  flex-direction: column;
}

.share-member-name {
  font-size: 13px;
  font-weight: 600;
  color: var(--color-text-primary);
}

.share-member-role {
  font-size: 11px;
  color: var(--color-text-secondary);
}

.share-send-btn {
  padding: 10px;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.share-send-btn:hover { background: #2563eb; }
.share-send-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* ===== Responsive ===== */
@media (max-width: 860px) {
  .calendar-layout {
    flex-direction: column;
  }

  .todo-section {
    width: 100%;
    min-width: 0;
    max-height: 50vh;
  }

  .header-date {
    display: none;
  }

  .filter-btn {
    padding: 6px 10px;
    font-size: 12px;
  }

  .day {
    min-height: 60px;
    padding: 4px;
  }

  .day-number {
    font-size: 12px;
  }

  .todo-tag {
    font-size: 8px;
    padding: 1px 3px;
  }

  .pounch-info .badge {
    font-size: 8px;
    padding: 1px 3px;
  }

  .more-todos {
    font-size: 8px;
  }

  .holiday-name {
    display: none;
  }
}

@media (max-width: 480px) {
  .dialog > .paper {
    width: 100%;
    height: 100%;
    max-height: 100vh;
    border-radius: 0;
    padding: 16px;
  }

  .month-title {
    font-size: 20px;
  }

  .calendar-header {
    gap: 8px;
  }

  .header-right {
    width: 100%;
    justify-content: center;
  }

  .day {
    min-height: 48px;
    padding: 3px;
    border-radius: 4px;
  }

  .day-number {
    font-size: 11px;
  }

  .todo-tag, .pounch-info .badge {
    font-size: 7px;
  }

  .todo-section {
    max-height: 45vh;
  }
}
</style>